import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { EmailDto, EmailEntityType, EmailSlotDto, EntityEmailsResponse } from '@tww/shared';
import { fmtDate, fmtDateTime } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Panel, Skeleton, StatusPill } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { EmailModal } from './EmailModal';

const SLOT_PILL: Record<EmailSlotDto['state'], string> = { sent: 'bestaetigt', due: 'warn', draft: 'abstimmung', upcoming: 'muted', waiting: 'muted', na: 'muted' };
const STATUS_PILL: Record<EmailDto['status'], string> = { sent: 'bestaetigt', draft: 'abstimmung', failed: 'offen' };

/**
 * Tab „E-Mails“ (Plan 06 Abschnitt 5.1 rechts): Vorlagen der Aktion mit Zustand und „Prüfen und senden“,
 * Verlauf aller Mails (auch freie) mit Empfänger, Betreff, Datum, Anhängen, Versandweg. ?mail=<key> öffnet die Vorlage direkt.
 */
export function EmailPanel({ entityType, entityId, prepareBase, title = 'E-Mails' }: { entityType: EmailEntityType; entityId: number; prepareBase: string; title?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const write = can('emails', 'write');
  const [sp, setSp] = useSearchParams();
  const q = useQuery({ queryKey: ['entity-emails', entityType, entityId], queryFn: () => api.get<EntityEmailsResponse>(`${prepareBase}/emails`) });
  const [open, setOpen] = useState<number | null>(null);
  // Frisch angelegte freie E-Mail: unverändert geschlossen wird sie wieder verworfen
  const [fresh, setFresh] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const prepare = useMutation({
    mutationFn: (key: string) => api.post<EmailDto & { notes: string[] }>(`${prepareBase}/emails/prepare`, { template_key: key }),
    onSuccess: (m) => {
      for (const n of m.notes ?? []) toast(n, n.startsWith('Kein') || n.includes('nicht') || n.includes('fehlt') || n.includes('Leere') ? 'error' : 'ok');
      qc.invalidateQueries({ queryKey: ['entity-emails', entityType, entityId] });
      qc.invalidateQueries({ queryKey: ['action-documents', entityId] });
      setOpen(m.id);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const free = useMutation({
    mutationFn: () => api.post<EmailDto>('/emails', { entity_type: entityType, entity_id: entityId }),
    onSuccess: (m) => {
      qc.invalidateQueries({ queryKey: ['entity-emails', entityType, entityId] });
      setFresh(m.id);
      setOpen(m.id);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const mailParam = sp.get('mail');
  // ?mail_id=<id>: eben angelegte E-Mail öffnen (z. B. an die Pflanzlotsen)
  const mailIdParam = sp.get('mail_id');
  useEffect(() => {
    if (!mailIdParam) return;
    const n = new URLSearchParams(sp);
    n.delete('mail_id');
    setSp(n, { replace: true });
    setFresh(Number(mailIdParam));
    setOpen(Number(mailIdParam));
  }, [mailIdParam]);
  useEffect(() => {
    if (!mailParam || !q.data || !write || prepare.isPending) return;
    const slot = q.data.slots.find((s) => s.template_key === mailParam);
    const n = new URLSearchParams(sp);
    n.delete('mail');
    setSp(n, { replace: true });
    if (slot?.email_id) setOpen(slot.email_id);
    else if (slot) prepare.mutate(slot.template_key);
  }, [mailParam, q.data]);
  const history = q.data?.history ?? [];
  const shown = showAll ? history : history.slice(0, 4);
  return (
    <Panel
      title={title}
      action={
        write && (
          <button type="button" className="btn sm" onClick={() => free.mutate()} disabled={free.isPending}>
            <Icon name="plus" /> Freie E-Mail
          </button>
        )
      }
    >
      {q.isLoading ? (
        <Skeleton rows={4} />
      ) : (
        <>
          {(q.data?.slots.length ?? 0) > 0 && (
            <ul className="docs" style={{ padding: '0 20px' }}>
              {q.data!.slots.map((s) => (
                <li key={s.template_key} className="docrow">
                  <span className="nm">
                    <span className="ttl">
                      {s.name} <StatusPill code={SLOT_PILL[s.state]} label={s.state === 'sent' ? 'gesendet' : s.state === 'due' ? 'fällig' : s.state === 'draft' ? 'Entwurf' : s.state === 'upcoming' ? 'geplant' : s.state === 'na' ? 'entfällt' : 'wartet'} />
                    </span>
                    <span>
                      {s.state === 'sent' ? `${s.state_label}${s.sent_by ? ` von ${s.sent_by}` : ''}` : s.state_label}
                      {s.state !== 'sent' && s.due_date && s.state !== 'due' && s.state !== 'upcoming' ? ` · ${fmtDate(s.due_date)}` : ''}
                    </span>
                  </span>
                  <span className="ext">
                    {s.email_id ? (
                      <button type="button" className="btn sm" onClick={() => setOpen(s.email_id)}>
                        {s.state === 'sent' ? 'Ansehen' : 'Prüfen und senden'}
                      </button>
                    ) : (
                      write && s.state !== 'na' && (
                        <button type="button" className="btn sm" onClick={() => prepare.mutate(s.template_key)} disabled={prepare.isPending}>
                          Prüfen und senden
                        </button>
                      )
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="pb" style={{ paddingTop: q.data?.slots.length ? 10 : undefined }}>
            <div className="mut small" style={{ marginBottom: 8 }}>
              Verlauf{history.length ? ` · ${history.length}` : ''}
            </div>
            {history.length === 0 ? (
              <div className="mut small">Noch keine E-Mail an diesem Datensatz.</div>
            ) : (
              <ul className="list mail-hist">
                {shown.map((m) => (
                  <li key={m.id}>
                    <span className="k">{m.sent_at ? fmtDate(m.sent_at) : fmtDate(m.created_at)}</span>
                    <span className="body">
                      <span className="ttl">
                        <button type="button" className="btn link-like" onClick={() => setOpen(m.id)}>
                          {m.subject || <span className="mut">ohne Betreff</span>}
                        </button>
                        <StatusPill code={STATUS_PILL[m.status]} label={m.status_label} />
                      </span>
                      <span className="sm">
                        {m.to.length ? `an ${m.to.map((a) => a.replace(/\s*<.*>$/, '')).join(', ')}` : 'ohne Empfänger'}
                        {m.attachments.length ? ` · ${m.attachments.length} Anhang${m.attachments.length > 1 ? 'e' : ''}` : ''}
                        {m.provider_label ? ` · ${m.provider_label}` : ''}
                        {m.sent_at ? ` · ${fmtDateTime(m.sent_at)}${m.sent_by ? ` von ${m.sent_by.initials ?? m.sent_by.display_name}` : ''}` : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {history.length > 4 && (
              <button type="button" className="btn sm" style={{ marginTop: 8 }} onClick={() => setShowAll((s) => !s)}>
                {showAll ? 'weniger anzeigen' : `alle ${history.length} anzeigen`}
              </button>
            )}
          </div>
        </>
      )}
      {open != null && <EmailModal emailId={open} entityType={entityType} entityId={entityId} discardIfUntouched={fresh === open} onClose={() => (setOpen(null), setFresh(null))} />}
    </Panel>
  );
}
